import React from 'react';
import type { RadioProps } from '.';
import { removeNonHTMLProps, safeInvoke } from '../../utils';
import { styled } from '@shared/joykit/packages/core/common/styledComponents';
import { CHOICE_INDICATOR } from '../../common';

// `activeColor` only styles the input below; it is not an HTML attribute, so it must not reach the DOM.
const Input: React.FC<RadioProps> = ({ activeColor, ...props }) => (
  <input
    aria-checked={props.checked}
    {...removeNonHTMLProps(props)}
    onChange={e => {
      safeInvoke(props.onChange, e);
      // e.currentTarget.focus();
    }}
    checked={props.checked}
    type={'radio'}
  />
);

export const StyledInput = styled(Input)`
  :enabled {
    /*
     * The control had no focus ring at all -- only a 1px border-colour shift on the indicator, which
     * is the same treatment it gives :active. The ring is offset for the same reason the checkbox's
     * is: it has to stay visible once the indicator fills. activeColor is honoured so guest
     * event-themed surfaces keep their accent, as the colour system allows.
     * See doc/field-focus-consistency/PLAN.md.
     */
    :focus-visible ~ .${CHOICE_INDICATOR} {
      outline: 2px solid ${props => props.activeColor || props.theme.colors.inputBorderFocused};
      outline-offset: 2px;
    }

    /*
     * There is deliberately no :focus rule here. The old one shifted the indicator's border to
     * fillDark -- primary8, a purple -- and :focus fires on mouse click too, so clicking a radio
     * left a purple hairline behind with no ring. With :focus-visible now painting the ring, the
     * :focus branch only re-introduced a brand colour onto a neutral control. Press feedback stays
     * on :active.
     */
    :active ~ .${CHOICE_INDICATOR} {
      border-color: ${props => props.activeColor || props.theme.colors.fillDark};
    }
  }

  :checked {
    & ~ .${CHOICE_INDICATOR} {
      ::after {
        background-color: ${props => props.activeColor};
      }
    }
  }
`;
